import { DeleteOutlineOutlined, EditOutlined, SearchOutlined } from "@mui/icons-material";
import { Box, Chip, CircularProgress, IconButton, InputAdornment, Paper, Stack, TextField, Typography } from "@mui/material";
import { useCallback } from "react";
import { BaseModalComponent } from "../../common/BaseModalComponent";
import { useDialog } from "../../ui/dialogs/useDialog";
import { IFieldDefinition, IFormState, IWorkflowBaseRecord, WorkflowEditorMode } from "../interfaces";
import { EditRecordModalContent } from "./EditModalRecordContent";
import { RecordCard } from "./RecordCard";
import { RelationRecordCard } from "./RelationRecordCard";

interface ICatalogManagerProps {
  title: string;
  records: IWorkflowBaseRecord[];
  selectedRecordUid: string | null;
  onSelectRecord: (uid: string) => void;
  loading?: boolean;
  error?: string | null;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  fields?: IFieldDefinition<any>[];
  onEditSaved?: (record: IWorkflowBaseRecord, initialValues: IFormState, values: IFormState) => Promise<void> | void;
  onDeleteRecord?: (recordUid: string) => void | Promise<void>;
  allowDelete?: boolean;
  deletingRecordUid?: string | null;
  nodeMode: WorkflowEditorMode;

  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
}

export const CatalogManager = (props: ICatalogManagerProps) => {
  const {
    title,
    records,
    selectedRecordUid,
    onSelectRecord,
    loading = false,
    error = null,
    hasMore = false,
    isFetchingMore = false,
    onLoadMore,
    fields = [],
    onEditSaved,
    onDeleteRecord,
    allowDelete = false,
    deletingRecordUid = null,
    nodeMode,
    searchValue = "",
    onSearchChange,
    searchPlaceholder = "Search by name...",
  } = props;

  const selectedRecord = records.find((record) => record.uid === selectedRecordUid) ?? null;

  const { showDialog, DialogComponent } = useDialog();

  const handleLeftScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const threshold = 80;

      const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;

      if (isNearBottom && hasMore && !isFetchingMore && onLoadMore) {
        onLoadMore();
      }
    },
    [hasMore, isFetchingMore, onLoadMore],
  );

  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>, record: IWorkflowBaseRecord) => {
    event.stopPropagation();

    showDialog({
      type: "confirm",
      title: "Delete Record",
      message: `Delete "${record.name}"? This action cannot be undone and may affect related data.`,
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      onConfirm: async () => {
        await onDeleteRecord?.(record.uid);
      },
      onCancel: () => {},
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        height: "100%",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          width: "40%",
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          overflow: "hidden",
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 3,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexShrink: 0 }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {title}
              </Typography>
            </Box>
          </Stack>

          {onSearchChange ? (
            <TextField
              size="small"
              fullWidth
              value={searchValue}
              placeholder={searchPlaceholder}
              onChange={(event) => onSearchChange(event.target.value)}
              sx={{ mb: 2, flexShrink: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          ) : null}

          <Box
            onScroll={handleLeftScroll}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
            }}
          >
            {loading ? (
              <Box sx={{ py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Loading records...
                </Typography>
              </Box>
            ) : null}

            {!loading && error ? (
              <Box sx={{ py: 3 }}>
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              </Box>
            ) : null}

            {!loading && !error && records.length === 0 ? (
              <Box sx={{ py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No records found.
                </Typography>
              </Box>
            ) : null}

            {!loading && !error && records.length > 0 ? (
              <Stack spacing={1.5} sx={{ pb: isFetchingMore ? 1 : 0 }}>
                {records.map((record) => {
                  const isSelected = selectedRecordUid === record.uid;

                  const trailing = (
                    <Stack direction="row" spacing={0.5}>
                      <BaseModalComponent
                        modalKey={`Superadmin-SectorWorkflows-Edit-${record.uid}`}
                        modalLabel={`Edit ${record.name}`}
                        defaultSizes={{ width: 520, height: 620 }}
                        elementTrigger={
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        }
                      >
                        <EditRecordModalContent
                          title={title}
                          fields={fields ?? []}
                          record={record}
                          onSaved={async (currentRecord, initialValues, values) => {
                            await onEditSaved?.(currentRecord, initialValues, values);
                          }}
                        />
                      </BaseModalComponent>

                      {allowDelete ? (
                        <IconButton size="small" onClick={(event) => handleDeleteClick(event, record)} disabled={deletingRecordUid === record.uid}>
                          {deletingRecordUid === record.uid ? <CircularProgress size={18} /> : <DeleteOutlineOutlined fontSize="small" />}
                        </IconButton>
                      ) : null}
                    </Stack>
                  );

                  const isRelationMode = nodeMode === WorkflowEditorMode.Relation;

                  if (isRelationMode) {
                    return (
                      <Box key={record.uid} onClick={() => onSelectRecord(record.uid)} sx={{ cursor: "pointer" }}>
                        <RelationRecordCard record={record} selected={isSelected} trailing={trailing} fields={fields ?? []} />
                      </Box>
                    );
                  }

                  return (
                    <RecordCard
                      key={record.uid}
                      title={record.name}
                      subtitle={record.description}
                      selected={isSelected}
                      onClick={() => onSelectRecord(record.uid)}
                      trailing={trailing}
                    />
                  );
                })}
              </Stack>
            ) : null}
          </Box>

          {isFetchingMore ? (
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 1.5,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <CircularProgress size={22} />
            </Box>
          ) : null}
        </Paper>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          overflow: "hidden",
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 3,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1, flexShrink: 0 }}>
            Details Preview
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexShrink: 0 }}>
            This panel will later host the selected record details, audit info and shortcuts to related editors.
          </Typography>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {!selectedRecord ? (
              <Typography variant="body2" color="text.secondary">
                Select a record to preview its details.
              </Typography>
            ) : (
              <Stack spacing={2}>
                <RecordCard
                  title={selectedRecord.name}
                  subtitle={selectedRecord.description}
                  imageUrl={selectedRecord.imageUrl}
                  record={selectedRecord}
                  fields={fields}
                  selected
                />
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>
      {DialogComponent}
    </Box>
  );
};
